import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { fetchRecomputeProgress } from '@/api/endpoints/recompute';
import { useTileRevision } from '@/stores/TileRevisionContext';
import type { RecomputeProgress } from '@/types/recompute';

const ACTIVE_POLL_MS = 2000;
const IDLE_POLL_MS = 15000;

interface RecomputeProgressValue {
  readonly progress: RecomputeProgress | null;
  readonly error: string | null;
  /**
   * Signals that `count` routes were just flagged stale (barrier edit, bulk
   * action). Forces an immediate poll so the pending count — and thus the
   * "Recompute" button state — updates without waiting for the idle tick.
   * Marking stale does not start a job, so no `bumpRoutes` is implied here.
   */
  readonly notifyEnqueued: (count: number) => void;
  /**
   * Forces an immediate progress poll. Used right after manually starting a
   * recompute so the badge flips to the "running" state without waiting for
   * the idle tick.
   */
  readonly refresh: () => void;
}

const RecomputeProgressContext =
  createContext<RecomputeProgressValue | undefined>(undefined);

interface ProviderProps {
  readonly children: ReactNode;
}

export function RecomputeProgressProvider({ children }: ProviderProps) {
  const [progress, setProgress] = useState<RecomputeProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { bumpRoutes } = useTileRevision();

  // Sticky activity flag: flips true on any non-empty poll AND on
  // `notifyEnqueued(n > 0)`; resets to false only after we act on a
  // drain (poll sees 0). Immune to the inflight-poll / concurrent-notify
  // race that a `previousTotal` counter would suffer from.
  const sawActivityRef = useRef(false);
  const cancelledRef = useRef(false);
  const timerRef = useRef<number | null>(null);

  const doPoll = useCallback(async () => {
    try {
      const data = await fetchRecomputeProgress();
      if (cancelledRef.current) return;
      setProgress(data);
      setError(null);
      const running = data.status === 'running';
      if (running) {
        sawActivityRef.current = true;
      } else if (sawActivityRef.current) {
        // Job finished after known activity — refresh route geometries so
        // the client picks up any barrier_aware inserts/deletes.
        sawActivityRef.current = false;
        bumpRoutes();
      }
      _schedule(timerRef, doPoll, running ? ACTIVE_POLL_MS : IDLE_POLL_MS);
    } catch (err: unknown) {
      if (cancelledRef.current) return;
      setError(err instanceof Error ? err.message : 'unknown error');
      _schedule(timerRef, doPoll, IDLE_POLL_MS);
    }
  }, [bumpRoutes]);

  useEffect(() => {
    cancelledRef.current = false;
    doPoll();
    return () => {
      cancelledRef.current = true;
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [doPoll]);

  const notifyEnqueued = useCallback(
    (count: number) => {
      if (count <= 0) return;
      // Reset the next poll to fire immediately so the pending count and
      // the "Recompute" button state refresh right away.
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      doPoll();
    },
    [doPoll],
  );

  const refresh = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    doPoll();
  }, [doPoll]);

  const value = useMemo<RecomputeProgressValue>(
    () => ({ progress, error, notifyEnqueued, refresh }),
    [progress, error, notifyEnqueued, refresh],
  );

  return (
    <RecomputeProgressContext.Provider value={value}>
      {children}
    </RecomputeProgressContext.Provider>
  );
}

export function useRecomputeProgress(): RecomputeProgressValue {
  const ctx = useContext(RecomputeProgressContext);
  if (!ctx) {
    throw new Error(
      'useRecomputeProgress must be used within RecomputeProgressProvider',
    );
  }
  return ctx;
}

function _schedule(
  timerRef: React.MutableRefObject<number | null>,
  fn: () => void,
  delayMs: number,
): void {
  timerRef.current = window.setTimeout(fn, delayMs);
}
